import { QRCodeCanvas } from 'qrcode.react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableFilterMeta } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { useToast } from '../../../components/Toast';
import { Dropdown } from 'primereact/dropdown';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import "jspdf-autotable";
import { TieredMenu } from "primereact/tieredmenu";
import { exportExcel, exportQrPDF } from "../../../components/Export";
import sparepartService from './sparepart-service';
import { Sidebar } from 'primereact/sidebar';
import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};

interface RecordFilter {
    [key: string]: string | boolean | Date | null; // Adjust types as needed
}

const SparepartList: React.FC = () => {
    const [gridDataSource, setGridDataSource] = useState<any[]>([]);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);
    const [isValid, setIsValid] = useState<boolean>(true);
    const { showToast } = useToast();
    const menuRef = useRef<TieredMenu>(null);
    const location = useLocation();
    const [recordFilter, setRecordFilter] = useState<RecordFilter>({
        dateFrom: new Date(),
        dateTo: new Date(),
        searchValue: '',
        searchBy: "",
    });

    const searchByOptions = [
        { label: 'Serial Number', value: 'itemSn' },
        { label: 'Item Description', value: 'itemDesc' },
        { label: 'Location', value: 'itemLocation' },
        { label: 'SKU', value: 'itemSku' },
    ]
    const toast = useRef<Toast>(null);

    const [shouldFetch, setShouldFetch] = useState<boolean>(false);

    const [itemQuantity, setItemQuantity] = useState<any>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);


    const [isPopupInstockVisible, setIsPopupInstockVisible] = useState(false);
    const [isPopupOutstockVisible, setIsPopupOutstockVisible] = useState(false);

    const [printerName, setPrinterName] = useState<string>("");



    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await sparepartService.getClientState();
                setClientState(clientStateItem);

                const result = await sparepartService.getPrinterName("PRINTER_NAME");

                setPrinterName(result.parameterValue);

                const searchParam = await sparepartService.getSearchParam(location.pathname);
                if (searchParam) {
                    setRecordFilter(searchParam.recordFilter);
                }

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    useEffect(() => {
        if (clientState) {
            const fetchUserList = async () => {

                if (recordFilter?.dateFrom !== null && recordFilter?.dateTo !== null) {
                    if (recordFilter?.dateFrom > recordFilter?.dateTo) {
                        setIsValid(false);
                    } else {
                        setIsValid(true);

                    }
                }

                try {
                    if (recordFilter.searchValue && recordFilter.searchBy) {
                        if (recordFilter.searchBy !== "insertBy") {
                            recordFilter.dateFrom = null;
                            recordFilter.dateTo = null;
                        }
                    }

                    recordFilter.country = clientState.country;

                    sparepartService.setSearchParam(recordFilter, location.pathname);

                    sparepartService.findAll(recordFilter).then((data) => {
                        if (data) {
                            setGridDataSource(data);
                        } else {
                            setGridDataSource([]);
                        }


                        setShouldFetch(false);
                    });
                } catch (error) {
                    showToast(clientState.error);
                }
            };

            fetchUserList();
        }
    }, [clientState, shouldFetch]);

    function handleCreate(): void {
        navigate('/sparepart/sparepart-edit');
    }

    const handleEdit = (item: any) => {
        navigate('/sparepart/sparepart-edit', { state: { selectedItem: item } });
    };

    const handleView = (item: any) => {
        navigate('/sparepart/sparepart-view', { state: { selectedItem: item } });
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleCloseInstockPopup = () => {
        setIsPopupInstockVisible(false);  // Reset the item
    };

    const handleCloseOutstockPopup = () => {
        setIsPopupOutstockVisible(false);  // Reset the item
    };

    function handlePopupInstockCreate(rowData: any): void {
        setIsPopupInstockVisible(true);
        setSelectedItem({
            itemQuantity: 0,
            sparepartId: rowData.sparepartId,
        });
    }

    function handlePopupOutstockCreate(rowData: any): void {
        setIsPopupOutstockVisible(true);
        setSelectedItem({
            itemQuantity: 0,
            sparepartId: rowData.sparepartId,
        });
    }

    // printerName: get from your state, or pass as parameter if needed
    const printQRLabelZPL = async (qrValue: string, entity: string, printerName: string) => {
        function formatEntity(entity: any) {
            return entity.replace(" (", "\\& (");
        }

        const formattedEntity = formatEntity(entity);

        //printer zebra use this
        const zpl = `
           ^XA
^PW320
^LL240
^MD30
^PR4
^LH0,0

^FX ================= QR CODE =================
^FX QR approx 184x184. Center Point: X=160, Y=107
^FO79,15
^BQN,2,4,H
^FDHA,${qrValue}^FS

^FX ================= LOGO BACKGROUND (ERASER) =================
^FX Size: 52x52 dots (Tighter fit for a 40x40 logo)
^FX X: 160 (Center) - 26 (Half Width) = 134
^FX Y: 107 (Center) - 26 (Half Height) = 81
^FO140,81
^GB52,52,52,W,3^FS

^FX ================= LOGO OVERLAY =================
^FX NOTE: The logo canvas is 64x64, we center it at X=160 - 32 = 128
^FO140,79
^GFA,378,378,7,,,,L01,L01E,L01F,L01F8,L01F8,L01FC,L01FC,L01FC,L01FC,L01FC,L01FC,L01F8,L01F8,L01F,L01F,L01E,L01E,01FE001C01FC,03FF801C07FF,07FFE0181IF800JF0107IF800JFC10JFC00KF03JFC01KFCKFC0,L01,L01,L01,L018,L01C,L01C,L01E,L01F,L01F,L01F,L01F8,L01F8,L01FC,L01FC,L01FC,L01FC,L01FC,L01FC,L01F8,L01F,L01F,L01C,,,,,
^FS

^FX ================= TEXT =================
^FO62,192
^FB200,5,0,C
^A0N,25,25
^FD${formattedEntity}^FS

^XZ

            `;

        const item = {
            labelCode: zpl,
            printerName: printerName
        };

        const response = await fetch('http://localhost:3006/print', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ labelCode: zpl, printerName }),
        });
    };


    const handlePrint = async (rowData: any) => {

        let error: any = null;

        try {
            await printQRLabelZPL(rowData.qrValue, rowData.itemEntity, printerName);
        } catch (err: any) {
            if (!error) error = err;
        }

        if (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Print Error',
                detail: 'Printer not found or nova device offline',
            });
        } else {
            toast.current?.show({
                severity: 'success',
                summary: 'Printing',
                detail: `Printing QR codes.`,
            });
        }
    };

    const header = () => {

        const formatDate = (date: any): string => {
            if (!date || typeof date === "boolean") return "Invalid Date";
            return new Date(date).toLocaleDateString("en-GB");
        };

        const fileName = `QR Generation ${formatDate(recordFilter?.dateFrom)} - ${formatDate(recordFilter?.dateTo)}`;

        const exportOptions: any[] = [
            {
                label: "Export to Excel",
                icon: "pi pi-file-excel",
                command: () => exportExcel(fileName),
            },
            {
                label: "Export to PDF",
                icon: "pi pi-file-pdf",
                command: () => exportQrPDF(fileName),
            },
        ];

        return (
            <div>
                <div className="flex justify-content-between">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.3em' }}>
                        <span className="p-float-label">
                            <InputText type="text" id="inputtext" value={globalFilterValue} onChange={onGlobalFilterChange} />
                            <label htmlFor="inputtext">Keyword Search</label>
                        </span>
                    </div>
                    <div>
                        <TieredMenu model={exportOptions} popup ref={menuRef} />
                        {/* <Button label="Excel" icon="pi pi-file-excel" className="p-button-success" style={{ marginRight: '.5em' }} onClick={() => exportExcel(fileName)} /> */}
                        {/* <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger" style={{ marginRight: '.5em' }} onClick={() => exportQrPDF(fileName)} /> */}
                        <Button label="New Spare Part" icon="pi pi-plus" style={{ marginRight: '.5em' }} onClick={() => handleCreate()} />

                    </div>
                </div>
            </div>
        );
    };


    const actionTemplate = (rowData: any) => (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Toast ref={toast} position="bottom-center" />
            <Button
                label="Edit"
                icon="pi pi-pencil"
                className="p-button-info p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handleEdit(rowData)}
            />
            <Button
                label="In"
                icon="pi pi-arrow-down"
                className="p-button-success p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handlePopupInstockCreate(rowData)}
            />
            <Button
                label="Out"
                icon="pi pi-arrow-up"
                className="p-button-danger p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handlePopupOutstockCreate(rowData)}
            />
            <Button
                label="Print QR"
                icon="pi pi-qrcode"
                className="p-button-warning p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handlePrint(rowData)}
            />
        </div>
    );

    const recordFilterTemplate = () => {
        const handleRecordFilterChange = (e: any) => {
            const { id, value } = e.target;
            setRecordFilter({
                ...recordFilter,
                [id]: value
            });
        };


        const handleInputChange = (e: any) => {
            const { id, value } = e.target;
            setRecordFilter({
                ...recordFilter,
                [id]: value
            });
        };

        const handleDropdownChange = (e: any) => {
            const { id, value } = e.target;
            setRecordFilter({
                ...recordFilter,
                [id]: value
            });
        };

        const handleSearch = () => {
            setShouldFetch(true);
        };

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2em' }}>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem' }}>
                        <Calendar
                            id="dateFrom"
                            value={recordFilter?.dateFrom && typeof recordFilter?.dateFrom !== 'boolean' ? new Date(recordFilter?.dateFrom) : null}
                            onChange={handleRecordFilterChange}
                            dateFormat="dd M yy"
                            showIcon
                            className={isValid ? '' : 'p-invalid'}
                        >
                        </Calendar>
                        <label style={{ paddingTop: '0.2rem' }}>Date From</label>
                    </div>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>
                        <Calendar
                            id="dateTo"
                            value={recordFilter?.dateTo && typeof recordFilter?.dateTo !== 'boolean' ? new Date(recordFilter?.dateTo) : null}
                            onChange={handleRecordFilterChange}
                            dateFormat="dd M yy"
                            showIcon
                        >
                        </Calendar>
                        <label style={{ paddingTop: '0.2rem' }}>Date To</label>
                    </div>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>
                        <Dropdown
                            id="searchBy"
                            value={recordFilter?.searchBy || ''}
                            onChange={handleDropdownChange}
                            options={searchByOptions}
                            optionLabel="label"
                            placeholder="Select One"
                            optionValue="value"
                            showClear
                        >
                        </Dropdown>
                        <label style={{ paddingTop: '0.2rem' }}>Search By</label>
                    </div>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>
                        <InputText
                            id="searchValue"
                            value={recordFilter?.searchValue != null ? String(recordFilter.searchValue) : ''}
                            onChange={handleInputChange}
                        />
                        <label style={{ paddingTop: '0.2rem' }}>Search Value</label>
                    </div>
                    <div>
                        <Button
                            label='Search'
                            icon="pi pi-search"
                            onClick={handleSearch}
                            severity='info'
                        />
                    </div>
                </div>
                {!isValid && (
                    <small id="dateFrom-help" className="p-error">
                        Date From must be earlier than or equal to Date To
                    </small>
                )}

            </div>
        )
    };

    const dialogInstockFooter = () => {

        function submit(): void {

            let item = {
                modifiedBy: clientState.fullName,
                itemQuantity: selectedItem?.itemQuantity,
                sparepartId: selectedItem?.sparepartId,
            }

            sparepartService.instock(item).then(() => {
                setShouldFetch(true);
                clientState.success.detail = 'Already Save Record';
                toast.current?.show(clientState.success);
                handleCloseInstockPopup();
            }).catch((error) => {
                const errorMessage = error.response?.data?.message || 'Failed to create record.';
                clientState.error.detail = errorMessage;
                toast.current?.show(clientState.error);
            });
        }
        function handleBack(): void {
            handleCloseInstockPopup();
        }

        return (
            <>

                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Back" icon="pi pi-reply" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    const dialogOutstockFooter = () => {

        function submit(): void {

            let item = {
                modifiedBy: clientState.fullName,
                itemQuantity: selectedItem?.itemQuantity,
                sparepartId: selectedItem?.sparepartId,
                remark: selectedItem?.remark,
            }

            sparepartService.outstock(item).then(() => {
                setShouldFetch(true);
                clientState.success.detail = 'Already Save Record';
                toast.current?.show(clientState.success);
                handleCloseOutstockPopup();
            }).catch((error) => {
                const errorMessage = error.response?.data?.message || 'Failed to create record.';
                clientState.error.detail = errorMessage;
                toast.current?.show(clientState.error);
            });
        }
        function handleBack(): void {
            handleCloseOutstockPopup();
        }

        return (
            <>

                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Back" icon="pi pi-reply" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };


    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Spare Parts</h5>
                    <BreadcrumbComponent />
                </div>

                <Sidebar
                    visible={isPopupInstockVisible}
                    onHide={() => setIsPopupInstockVisible(false)}
                    baseZIndex={1000}
                    position="right"
                    style={{ width: '600px' }}
                    showCloseIcon={false}
                >
                    <div className='card-sidebar card-sidebar-green'>
                        <div className='header-sidebar'>
                            <i className="pi pi-arrow-down-left mr-2" aria-hidden="true" />
                            Instock
                        </div>
                    </div>
                    <div className='card-sidebar '>
                        <div className='col-12'>
                            <div className="p-fluid form-grid-edit grid">
                                <div className="f-field-full">
                                    <label className='f-label-unwrap'>Instock Quantity</label>
                                    <div className="f-input">
                                        <InputNumber
                                            inputId="itemQuantity"
                                            name="itemQuantity"
                                            showButtons
                                            mode="decimal"
                                            value={selectedItem?.itemQuantity}
                                            onValueChange={(e) =>
                                                setSelectedItem({ ...selectedItem, itemQuantity: e.value })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <br />
                            </div>
                            <Toolbar end={dialogInstockFooter}></Toolbar>
                        </div>
                    </div>
                </Sidebar>

                <Sidebar
                    visible={isPopupOutstockVisible}
                    onHide={() => setIsPopupOutstockVisible(false)}
                    baseZIndex={1000}
                    position="right"
                    style={{ width: '600px', maxWidth: '100vw' }}
                    showCloseIcon={false}
                >
                    <div className='card-sidebar card-sidebar-red'>
                        <div className='header-sidebar'>
                            <i className="pi pi-arrow-up-right mr-2" aria-hidden="true" />
                            Outstock
                        </div>
                    </div>
                    <div className='card-sidebar '>
                        <div className='col-12'>
                            <div className="p-fluid form-grid-edit grid">
                                <div className="f-field-full">
                                    <label className='f-label-unwrap'>Outstock Quantity</label>
                                    <div className="f-input">
                                        <InputNumber
                                            inputId="itemQuantity"
                                            name="itemQuantity"
                                            showButtons
                                            mode="decimal"
                                            value={selectedItem?.itemQuantity}
                                            onValueChange={(e) =>
                                                setSelectedItem({ ...selectedItem, itemQuantity: e.value })
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="f-field-full">
                                    <label className='f-label-unwrap'>Remark</label>
                                    <div className="f-input">
                                        <InputTextarea
                                            id="remark"
                                            name="remark"
                                            value={selectedItem?.remark || ''}
                                            onChange={(e) =>
                                                setSelectedItem({ ...selectedItem, remark: e.target.value })
                                            }
                                            rows={5}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <br />
                            </div>
                            <Toolbar end={dialogOutstockFooter}></Toolbar>
                        </div>
                    </div>
                </Sidebar>

                <div className='card'>
                    {/* Filter Date From and Date To */}
                    <Toolbar start={recordFilterTemplate}></Toolbar>
                    <div><br></br></div>
                    <>
                        <DataTable
                            value={gridDataSource}
                            showGridlines
                            scrollable
                            scrollHeight='auto'
                            paginator rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            filters={filters}
                            globalFilterFields={['qrBatchId']}
                            header={header}
                            sortField='modifiedDate'
                            sortOrder={-1}
                            dataKey="qrBatchId"
                        >
                            <Column
                                header="No."
                                style={{ width: '5%' }}
                                body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                                headerClassName="list-header"
                            ></Column>
                            <Column body={actionTemplate} header="Actions" style={{ width: '20%' }} headerClassName="list-header"></Column>
                            {/* <Column field="insertDate" header="Created Date" body={(rowData) => new Date(rowData.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })} headerClassName="list-header"></Column>
                            <Column field="insertBy" header="Created By" headerClassName="list-header"></Column> */}
                            <Column field="itemDesc" header="Item Description" headerClassName="list-header"></Column>
                            <Column field="itemBrand" header="Brand" headerClassName="list-header"></Column>
                            <Column field="itemEntity" header="Item Entity" headerClassName="list-header"></Column>
                            <Column field="itemLocation" header="Location" headerClassName="list-header"></Column>
                            <Column field="itemSku" header="SKU" headerClassName="list-header"></Column>
                            <Column field="itemSn" header="Serial Number" headerClassName="list-header"></Column>
                            <Column field="itemRemark" header="Remark" headerClassName="list-header" bodyStyle={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', tabSize: 8 }}></Column>
                            <Column field="itemQuantity" header="Quantity" headerClassName="list-header"></Column>
                        </DataTable>
                    </>
                </div>
            </div>
        </div >
    );
};

export default SparepartList;

